import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="About RESILIGENT | Leadership Development Firm Nigeria & Africa"
        description="RESILIGENT is Nigeria's premier leadership and organisational development firm. Pan-African expertise in executive coaching, leadership diagnostics, board advisory, and culture transformation."
        keywords="leadership development company Nigeria, executive coaching firm Lagos, organisational development consultancy Africa, leadership consulting Nigeria"
        canonical="/about"
      />
      <Header />
      
      <main>
        {/* Hero Section - Clean and tight */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.85) 50%, rgba(10, 10, 10, 0.6) 100%), url('/opt/photo/resiligent-office.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
                About RESILIGENT
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl xl:text-2xl font-medium leading-relaxed">
                Advisory-led leadership and organisational development for Africa and beyond.
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            ACT 1: CONVICTION - Why RESILIGENT exists
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            
            {/* Section Heading */}
            <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-12 leading-tight">
              Why RESILIGENT exists
            </h2>
            
            {/* Card 1: Opening Quote + Context */}
            <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-8 lg:p-12 mb-6 transition-all duration-300 hover:shadow-xl hover:scale-[1.01] hover:border-slate-300 animate-float-1">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Left: Quote */}
                <div>
                  <p className="text-slate-800 text-xl sm:text-2xl lg:text-3xl font-medium leading-snug">
                    "We were tired of consulting that looked impressive, but left leaders alone with execution."
                  </p>
                </div>
                
                {/* Right: Context */}
                <div className="space-y-4">
                  <p className="text-slate-700 text-base lg:text-lg leading-[1.8]">
                    RESILIGENT was founded in 2010 out of a growing frustration with how leadership work was being done.
                  </p>
                  <p className="text-slate-600 text-base lg:text-lg leading-[1.8]">
                    Too many consulting engagements delivered elegant frameworks and well-received programmes, only to leave organisations struggling with execution once the consultants had moved on.
                  </p>
                  <p className="text-slate-600 text-base lg:text-lg leading-[1.8]">
                    Leaders were left inspired, but unsupported. Aware, but unchanged.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card 2: The Problem + What We Rejected */}
            <div className="bg-slate-100 border-2 border-slate-200 rounded-2xl p-8 lg:p-12 mb-6 transition-all duration-300 hover:shadow-xl hover:scale-[1.01] hover:border-slate-300 animate-float-2">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Left: Assertion */}
                <div>
                  <p className="text-slate-800 text-xl lg:text-2xl font-medium leading-relaxed mb-2">
                    The problem was not a lack of insight.
                  </p>
                  <p className="text-slate-800 text-xl lg:text-2xl font-medium leading-relaxed">
                    It was a lack of ownership for what happened next.
                  </p>
                </div>
                
                {/* Right: What We Rejected */}
                <div>
                  <p className="text-slate-700 text-base lg:text-lg leading-[1.8] mb-4">
                    We had seen enough of:
                  </p>
                  <div className="space-y-2">
                    <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                      Consulting engagements that ended at insight.
                    </p>
                    <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                      Leadership programmes imported without adaptation.
                    </p>
                    <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                      Organisations left to figure out execution on their own.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Card 3: Africa Context */}
            <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-8 lg:p-12 mb-6 transition-all duration-300 hover:shadow-xl hover:scale-[1.01] hover:border-slate-300 animate-float-3">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Left: Failure Amplified */}
                <div>
                  <p className="text-slate-600 text-base lg:text-lg leading-[1.8]">
                    This failure was amplified across African organisations, where leadership models were being imported wholesale, designed for different operating realities, different power dynamics, and different cultural contexts.
                  </p>
                </div>
                
                {/* Right: Wrong Conclusion */}
                <div>
                  <p className="text-slate-600 text-base lg:text-lg leading-[1.8] mb-4">
                    They sounded sophisticated, but they rarely fit. And when they failed, the explanation was placed on the organisation, not the model.
                  </p>
                  <p className="text-slate-700 text-base lg:text-lg leading-[1.8] font-medium">
                    We believed that was the wrong conclusion.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card 4: Proof Point + Closing - Navy Blue */}
            <div className="bg-[#1e3a5f] border-2 border-[#2a4a73] rounded-2xl p-8 lg:p-12 transition-all duration-300 hover:shadow-2xl hover:scale-[1.01] hover:shadow-[#1e3a5f]/30 animate-float-navy">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Left: Africa Assertion */}
                <div>
                  <p className="text-white text-xl lg:text-2xl font-medium leading-relaxed mb-2">
                    Africa was not a limitation.
                  </p>
                  <p className="text-white text-xl lg:text-2xl font-medium leading-relaxed">
                    It was the proof point.
                  </p>
                </div>
                
                {/* Right: Closing */}
                <div>
                  <p className="text-slate-300 text-base lg:text-lg leading-[1.8]">
                    This is the standard we hold ourselves to, and the lens through which all our work is delivered.
                  </p>
                </div>
              </div>
            </div>
            
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            ACT 2: OPERATING PHILOSOPHY - Leadership as a System
        ═══════════════════════════════════════════════════════════════ */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2693d0]/20 to-transparent" />
          
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              
              {/* Left: Content */}
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Leadership is not an event.<br />
                  <span className="text-[#2693d0]">It is a system.</span>
                </h2>
                
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  We treat leadership not as a series of interventions, but as a connected system that links insight to behaviour, culture to execution, and individual growth to organisational performance.
                </p>
              </div>
              
              {/* Right: System Visual */}
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img 
                    src="/opt/photo/ext/about-teamwork-900.webp"
                    alt="Interconnected system representing leadership integration"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative accent */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#2693d0]/10 rounded-full blur-2xl" />
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-[#2693d0]/10 rounded-full blur-xl" />
              </div>
              
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2693d0]/20 to-transparent" />
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            ACT 3: PROOF OF SERIOUSNESS - What Makes RESILIGENT Different
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-[#0a0a0a] py-16 lg:py-24">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-12 leading-tight">
              What makes RESILIGENT different
            </h2>
            
            {/* 4 Signal Cards */}
            <div className="grid sm:grid-cols-2 gap-6 lg:gap-8 mb-12">
              
              {/* Card 1: MCC Coaches */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 lg:p-8 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#2693d0]/30 hover:border-[#2693d0]/50 hover:bg-white/10 cursor-default animate-float-dark">
                <h3 className="text-white font-semibold text-lg lg:text-xl mb-3">
                  Multiple ICF Master Certified Coaches
                </h3>
                <p className="text-slate-400 text-base leading-relaxed">
                  One of the only firms in West Africa with four MCCs on faculty.
                </p>
              </div>
              
              {/* Card 2: Proprietary System */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 lg:p-8 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#2693d0]/30 hover:border-[#2693d0]/50 hover:bg-white/10 cursor-default animate-float-1">
                <h3 className="text-white font-semibold text-lg lg:text-xl mb-3">
                  Proprietary leadership intelligence
                </h3>
                <p className="text-slate-400 text-base leading-relaxed">
                  <span className="text-[#2693d0]">Impact AS Plus™</span>: our own diagnostics, platform, and measurement system.
                  <a href="/impact-as-plus" className="text-[#2693d0] hover:underline ml-1">Learn more →</a>
                </p>
              </div>
              
              {/* Card 3: Global Standards */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 lg:p-8 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#2693d0]/30 hover:border-[#2693d0]/50 hover:bg-white/10 cursor-default animate-float-2">
                <h3 className="text-white font-semibold text-lg lg:text-xl mb-3">
                  Global standards, selectively integrated
                </h3>
                <p className="text-slate-400 text-base leading-relaxed">
                  Certified partners for Hogan, Everything DiSC®, and Leadership Circle Profile™.
                </p>
              </div>
              
              {/* Card 4: Sector Experience */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 lg:p-8 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#2693d0]/30 hover:border-[#2693d0]/50 hover:bg-white/10 cursor-default animate-float-3">
                <h3 className="text-white font-semibold text-lg lg:text-xl mb-3">
                  Deep sector experience
                </h3>
                <p className="text-slate-400 text-base leading-relaxed">
                  Financial services, energy, FMCG, telecoms, healthcare, and public sector, delivered with African fluency.
                </p>
              </div>
              
            </div>
            
            {/* Single reference line for coaching ecosystem */}
            <p className="text-slate-500 text-sm">
              Executive coaching for C-suite leaders is delivered through our sister firm, 
              <span className="text-slate-400"> AfriExecutive Coaching</span>.
            </p>
          </div>
        </section>

        {/* Founder Quote - Condensed */}
        <section className="bg-[#1e3a5f] py-14 lg:py-20">
          <div className="w-full max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <blockquote className="mb-8">
              <p className="text-white text-xl sm:text-2xl lg:text-3xl font-medium leading-relaxed">
                "You do not rise to the level of your ambition, you fall to the level of your systems."
              </p>
            </blockquote>
            
            <div className="border-t border-white/20 pt-6 inline-block">
              <p className="text-white font-semibold text-base">Remi Adebonojo, ICF MCC</p>
              <p className="text-slate-400 text-sm">Founder & Managing Partner</p>
            </div>
          </div>
        </section>

        {/* Minimal Footer Navigation */}
        <section className="bg-white py-12 border-t border-slate-100">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-center gap-6 sm:gap-12 text-center">
              <a href="/engagement" className="text-slate-600 hover:text-[#2693d0] text-base font-medium transition-colors">
                How organisations engage us →
              </a>
              <a href="/impact-as-plus" className="text-slate-600 hover:text-[#2693d0] text-base font-medium transition-colors">
                Explore Impact AS Plus™ →
              </a>
              <a href="/contact" className="text-slate-600 hover:text-[#2693d0] text-base font-medium transition-colors">
                Get in touch →
              </a>
            </div>
          </div>
        </section>
      </main>
      
      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default AboutPage;
